
'use client';

import { useAuth } from '@/hooks/useAuth';
import { Loader2, PlusCircle, Newspaper, Image as ImageIcon, Trash2, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { createNoticia, getAllNoticias, deleteNoticia, updateNoticia } from '@/lib/actions';
import { Noticia } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Image from '@/components/ui/Image';
import { Card } from '@/components/ui/card';

function NoticiaForm({
  onFormSuccess,
  noticiaToEdit,
}: {
  onFormSuccess: () => void;
  noticiaToEdit?: Noticia | null;
}) {
  const isEditing = !!noticiaToEdit;
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const action = isEditing && noticiaToEdit ? updateNoticia.bind(null, noticiaToEdit.id) : createNoticia;
    
    const result = await action(formData);
    
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({
        title: 'Error',
        description: result.message,
        variant: 'destructive',
      });
    }
    setIsSubmitting(false);
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Título</Label>
        <Input id="title" name="title" defaultValue={noticiaToEdit?.title} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="content">Contenido</Label>
        <RichTextarea id="content" name="content" rows={8} defaultValue={noticiaToEdit?.content} />
      </div>
       <div className="space-y-2">
        <Label htmlFor="imageUrl">URL de la Imagen (Opcional)</Label>
        <Input id="imageUrl" name="imageUrl" type="url" placeholder="https://i.imgur.com/..." defaultValue={noticiaToEdit?.imageUrl} />
      </div>
      <DialogFooter>
        <DialogClose asChild><Button variant="outline" type="button">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEditing ? 'Guardar Cambios' : 'Publicar Noticia'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function AdminNoticiasPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const [noticias, setNoticias] = useState<Noticia[]>([]);
  const [isLoadingNoticias, setIsLoadingNoticias] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [noticiaToEdit, setNoticiaToEdit] = useState<Noticia | null>(null);
  const { toast } = useToast();

  const fetchNoticias = async () => {
    setIsLoadingNoticias(true);
    const { noticias } = await getAllNoticias();
    setNoticias(noticias);
    setIsLoadingNoticias(false);
  };

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') {
      navigate('/jartinet');
    } else {
      fetchNoticias();
    }
  }, [user, userData, loading, navigate]);
  
  const handleFormSuccess = () => {
    setIsDialogOpen(false);
    setNoticiaToEdit(null);
    fetchNoticias();
  };
  
  const handleEditClick = (noticia: Noticia) => {
    setNoticiaToEdit(noticia);
    setIsDialogOpen(true);
  };

  const handleNewClick = () => {
    setNoticiaToEdit(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (noticiaId: string) => {
    const result = await deleteNoticia(noticiaId);
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      fetchNoticias();
    } else {
      toast({ title: 'Error', description: result.error, variant: 'destructive' });
    }
  };


  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="max-w-3xl">
          <h1 className="font-headline text-5xl font-bold text-primary">Gestionar Noticias</h1>
          <p className="mt-4 text-lg text-muted-foreground">Publica y gestiona las noticias de la web principal.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild><Button size="lg" onClick={handleNewClick}><PlusCircle className="mr-2 h-5 w-5" /> Nueva Noticia</Button></DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>{noticiaToEdit ? 'Editar Noticia' : 'Crear Nueva Noticia'}</DialogTitle>
              <DialogDescription>Rellena todos los campos para publicar una nueva noticia.</DialogDescription>
            </DialogHeader>
            <NoticiaForm onFormSuccess={handleFormSuccess} noticiaToEdit={noticiaToEdit} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-bold font-headline">Noticias Publicadas</h2>
        {isLoadingNoticias ? (
          <div className="flex justify-center items-center h-40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : noticias.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {noticias.map(noticia => (
              <Card key={noticia.id} className="flex flex-col">
                {noticia.imageUrl && (
                  <div className="relative w-full aspect-video rounded-t-lg overflow-hidden">
                    <Image src={noticia.imageUrl} alt={noticia.title} className="object-cover w-full h-full" />
                  </div>
                )}
                <div className="p-4 flex flex-col flex-grow">
                  <h3 className="font-bold text-lg leading-tight">{noticia.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 flex-grow line-clamp-3">{noticia.content}</p>
                  <p className="text-xs text-muted-foreground mt-4">{format(new Date(noticia.date), "dd/MM/yyyy", { locale: es })}</p>
                </div>
                 <div className="p-2 border-t flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEditClick(noticia)}><Edit className="h-4 w-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar esta noticia?</AlertDialogTitle>
                          <AlertDialogDescription>Esta acción es permanente y no se puede deshacer.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(noticia.id)} className="bg-destructive hover:bg-destructive/80">Confirmar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                 </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 border-2 border-dashed rounded-lg">
            <Newspaper className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-semibold">No hay noticias</h3>
            <p className="mt-1 text-sm text-muted-foreground">Crea tu primera noticia para empezar.</p>
          </div>
        )}
      </div>
    </div>
  );
}
